// src/pages/ClientEditPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
  Table,
  Modal,
} from "react-bootstrap";
import api from "../api";
import { useNavGuard } from "../hooks/useNavGuard";
import NavGuardModal from "../components/NavGuardModal";

const REFERRED_BY_OPTIONS = [
  "Church",
  "CMH",
  "DHHS",
  "Facebook",
  "MMCAA",
  "United Way",
  "Salvation Army",
  "Friend/Family",
  "Other Agency",
];

const COUNTY_OPTIONS = [
  "Clare",
  "Gladwin",
  "Isabella",
  "Mecosta",
  "Midland",
  "Missaukee",
  "Osceola",
];

const HELP_ALREADY_RECEIVED_OPTIONS = [
  "DHHS",
  "MMCAA",
  "Salvation Army",
  "Listening Ear",
  "Veterans",
  "Other",
];

const TRANSPORTATION_OPTIONS = ["Car/Truck", "Transit", "Family/Friends"];

// New: Denominations + Churches (suggestions, still free-text via datalist / Other)
const DENOMINATION_OPTIONS = [
  "Baptist",
  "Catholic",
  "Church of Christ",
  "Episcopal",
  "Evangelical",
  "Lutheran",
  "Methodist",
  "Non-denominational",
  "Pentecostal",
  "Presbyterian",
  "Reformed",
  "Seventh-day Adventist",
  "United Church of Christ (UCC)",
  "Other (type)",
];

// Suggestion list only (still free-text add). Replace with your real Clare County list if you want.
const CHURCH_SUGGESTIONS = [
  "St. Cecilia Catholic Church (Clare)",
  "Clare United Methodist Church",
  "Clare Church of the Nazarene",
  "First Baptist Church of Clare",
  "Clare Assembly of God",
  "Cornerstone Church (Clare area)",
  "Non-denominational (Other)",
];

const normalizeDateInput = (val) => {
  if (!val) return "";
  return String(val).slice(0, 10);
};

// Local (not UTC) "YYYY-MM-DD" — toISOString() would roll to the next day for
// anyone east of UTC and back a day for us in the evening.
const todayISO = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const getHousingTypeLabel = (ht) => ht?.name || ht?.label || ht?.description || "";

const EMPTY_LANDLORD_FORM = {
  id: null,
  name: "",
  phone: "",
  address: "",
  city: "",
  state: "MI",
  zipcode: "",
};

export default function ClientEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [form, setForm] = useState(null);
  const [dirty, setDirty] = useState(false);

  const { showModal, resolveGuard } = useNavGuard(dirty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Household members
  const [familyMembers, setFamilyMembers] = useState([]);
  const [fmLoading, setFmLoading] = useState(false);
  const [fmError, setFmError] = useState("");
  const [fmSuccess, setFmSuccess] = useState("");

  // Housing Types
  const [housingTypes, setHousingTypes] = useState([]);
  const [housingTypesLoading, setHousingTypesLoading] = useState(false);

  // Help Already Received / Applied (kept for later page)
  const [helpOtherText, setHelpOtherText] = useState("");

  // Landlord lookup + edit/add
  const [landlords, setLandlords] = useState([]);
  const [landlordsLoading, setLandlordsLoading] = useState(false);

  // What user sees/types in the landlord field
  const [landlordQuery, setLandlordQuery] = useState("");

  // Modal state
  const [showLandlordModal, setShowLandlordModal] = useState(false);
  const [landlordModalMode, setLandlordModalMode] = useState("add"); // "add" | "edit"
  const [landlordForm, setLandlordForm] = useState(EMPTY_LANDLORD_FORM);
  const [landlordModalSaving, setLandlordModalSaving] = useState(false);
  const [landlordModalError, setLandlordModalError] = useState("");

  const fetchLandlords = async (q = "") => {
    setLandlordsLoading(true);
    try {
      const res = await api.get("/clients/landlords", { params: q ? { q } : {} });
      const list = Array.isArray(res.data) ? res.data : res.data.items || [];
      setLandlords(list);
    } catch (e) {
      setLandlords([]);
    } finally {
      setLandlordsLoading(false);
    }
  };

  // Load client + household members
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setError("");
      setFmError("");

      try {
        setFmLoading(true);

        const [cRes, fmRes] = await Promise.all([
          api.get(`/clients/${id}`),
          api.get(`/clients/${id}/family-members`),
        ]);

        setClient(cRes.data);

        const har = Array.isArray(cRes.data.help_already_received)
          ? cRes.data.help_already_received
          : [];
        const otherDetail = har.find((v) => typeof v === "string" && v.startsWith("Other: "));
        setHelpOtherText(otherDetail ? otherDetail.replace(/^Other:\s*/, "") : "");

        setForm({
          ...cRes.data,
          intake_date: normalizeDateInput(cRes.data.intake_date),
          birth_date: normalizeDateInput(cRes.data.birth_date),
          ssn: cRes.data.ssn || "",
          no_phone: !!cRes.data.no_phone,
          functional_limitations: cRes.data.functional_limitations || "",
          safety_alerts: cRes.data.safety_alerts || "",
          special_circumstances: cRes.data.special_circumstances || "",
          salvation_army_help: !!cRes.data.salvation_army_help,
          salvation_army_help_date: normalizeDateInput(cRes.data.salvation_army_help_date),

          housing_type: cRes.data.housing_type ?? "",

          // landlord linkage
          landlord_id: cRes.data.landlord_id ?? "",

          // kept for later page
          help_already_received: har,
          family_help: !!cRes.data.family_help,
          family_help_amount:
            cRes.data.family_help_amount == null ? "" : String(cRes.data.family_help_amount),

          // tri-state booleans for selects: "" | true | false
          has_washer_dryer:
            cRes.data.has_washer_dryer === true
              ? true
              : cRes.data.has_washer_dryer === false
              ? false
              : "",

          has_valid_drivers_license:
            cRes.data.has_valid_drivers_license === true
              ? true
              : cRes.data.has_valid_drivers_license === false
              ? false
              : "",

          // backend uses where_laundry
          where_laundry: cRes.data.where_laundry || "",

          // backend uses transportation_type
          transportation_type: cRes.data.transportation_type || "",

          // Permanent rider category (base for each transportation request)
          rider_category: cRes.data.rider_category || "",

          uses_transit:
            cRes.data.uses_transit === true ? true : cRes.data.uses_transit === false ? false : "",
          transit_fare: cRes.data.transit_fare || "",

          // NEW: Pastor visit + denomination + church name
          wants_pastor_visit:
            cRes.data.wants_pastor_visit === true
              ? true
              : cRes.data.wants_pastor_visit === false
              ? false
              : "",
          church_denom_preferred: cRes.data.church_denom_preferred || "",
          church_name: cRes.data.church_name || "",
          is_veteran:
            cRes.data.is_veteran === true
              ? true
              : cRes.data.is_veteran === false
              ? false
              : "",
          veteran_names: cRes.data.veteran_names || "",
        });

        // display landlord name if API includes landlord object
        if (cRes.data.landlord?.name) setLandlordQuery(cRes.data.landlord.name);
        else setLandlordQuery("");

        setFamilyMembers(fmRes.data || []);
        setDirty(false);
      } catch (err) {
        setError("Failed to load client");
      } finally {
        setFmLoading(false);
        setLoading(false);
      }
    };

    if (id) loadAll();
  }, [id]);

  // Load housing types on mount
  useEffect(() => {
    const loadHousingTypes = async () => {
      setHousingTypesLoading(true);
      try {
        const res = await api.get("/clients/housing-types");
        const list = Array.isArray(res.data) ? res.data : res.data.items || [];
        setHousingTypes(list);
      } catch (e) {
        setHousingTypes([]);
      } finally {
        setHousingTypesLoading(false);
      }
    };
    loadHousingTypes();
  }, []);

  // Load landlord list on mount
  useEffect(() => {
    fetchLandlords("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setSuccess("");
  };

  const handleCheckbox = (field) => (e) => {
    const value = e.target.checked;
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setSuccess("");
  };

  // Tri-state boolean select helper supporting "" | true | false
  const handleBooleanSelect = (field) => (e) => {
    const v = e.target.value;
    const next = v === "yes" ? true : v === "no" ? false : "";
    setForm((prev) => ({ ...prev, [field]: next }));
    setDirty(true);
    setSuccess("");
  };

  // Help already received helpers (kept for later page)
  const toggleHelpAlreadyReceived = (label) => (e) => {
    const checked = e.target.checked;

    setForm((prev) => {
      const current = Array.isArray(prev.help_already_received) ? prev.help_already_received : [];
      const withoutOtherDetail = current.filter(
        (v) => !(typeof v === "string" && v.startsWith("Other: "))
      );

      if (label === "Other") {
        if (!checked) {
          const next = withoutOtherDetail.filter((v) => v !== "Other");
          return { ...prev, help_already_received: next };
        }
        const next = withoutOtherDetail.includes("Other")
          ? withoutOtherDetail
          : [...withoutOtherDetail, "Other"];
        return { ...prev, help_already_received: next };
      }

      if (checked) {
        if (withoutOtherDetail.includes(label)) return prev;
        return { ...prev, help_already_received: [...withoutOtherDetail, label] };
      }

      return { ...prev, help_already_received: withoutOtherDetail.filter((v) => v !== label) };
    });

    setDirty(true);
    setSuccess("");

    if (label === "Other" && !checked) setHelpOtherText("");
  };

  const isHelpChecked = (label) => {
    const arr = Array.isArray(form?.help_already_received) ? form.help_already_received : [];
    if (label === "Other") {
      return arr.includes("Other") || arr.some((v) => String(v).startsWith("Other: "));
    }
    return arr.includes(label);
  };

  // Checking the box defaults the date to today (the common case: staff are
  // recording help the client just told them about) but leaves it editable,
  // since the help may have been received weeks earlier. Unchecking clears it.
  const toggleSalvationArmyHelp = (e) => {
    const checked = e.target.checked;
    setForm((prev) => ({
      ...prev,
      salvation_army_help: checked,
      salvation_army_help_date: checked
        ? prev.salvation_army_help_date || todayISO()
        : "",
    }));
    setDirty(true);
    setSuccess("");
  };

  const setFamilyHelp = (yes) => {
    setForm((prev) => ({
      ...prev,
      family_help: !!yes,
      family_help_amount: yes ? prev.family_help_amount : "",
    }));
    setDirty(true);
    setSuccess("");
  };

  // Determine if landlord field should show based on housing type label
  const shouldShowLandlord = useMemo(() => {
    const idVal = form?.housing_type;
    if (idVal == null || idVal === "") return false;

    const ht = housingTypes.find((x) => String(x.id) === String(idVal));
    const label = (getHousingTypeLabel(ht) || "").toLowerCase();
    return label === "rent" || label === "rent - multi-tenant";
  }, [form?.housing_type, housingTypes]);

  // Laundry question should show only if washer/dryer === NO (false)
  const shouldShowWhereLaundry = useMemo(() => {
    return form?.has_washer_dryer === false;
  }, [form?.has_washer_dryer]);

  // Veteran name options: client + family members
  const veteranOptions = useMemo(() => {
    const names = [];
    const clientName = [form?.first_name, form?.last_name].filter(Boolean).join(" ").trim();
    if (clientName) names.push(clientName);
    familyMembers.forEach((m) => {
      const n = (m.full_name || "").trim();
      if (n) names.push(n);
    });
    return names;
  }, [form?.first_name, form?.last_name, familyMembers]);

  const selectedVeteranNames = useMemo(() => {
    if (!form?.veteran_names) return [];
    return form.veteran_names.split(",").map((n) => n.trim()).filter(Boolean);
  }, [form?.veteran_names]);

  const toggleVeteranName = (name) => {
    const current = selectedVeteranNames;
    const updated = current.includes(name)
      ? current.filter((n) => n !== name)
      : [...current, name];
    setForm((prev) => ({ ...prev, veteran_names: updated.join(", ") }));
    setDirty(true);
    setSuccess("");
  };

  // New: show denomination only if wants_pastor_visit === true
  const shouldShowDenomination = useMemo(() => {
    return form?.wants_pastor_visit === true;
  }, [form?.wants_pastor_visit]);

  // New: show church name only if attends_church_in_clare is checked
  const shouldShowChurchName = useMemo(() => {
    return !!form?.attends_church_in_clare;
  }, [form?.attends_church_in_clare]);

  // When "No Phone" is checked, clear the phone fields to avoid inconsistent data
  useEffect(() => {
    if (!form) return;
    if (form.no_phone && ((form.home_phone || "").trim() || (form.mobile_phone || "").trim())) {
      setForm((prev) => ({ ...prev, home_phone: "", mobile_phone: "" }));
      setDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.no_phone]);

  // If washer/dryer changes away from "No", clear where_laundry
  useEffect(() => {
    if (!form) return;
    if (!shouldShowWhereLaundry) {
      if ((form.where_laundry || "").trim()) {
        setForm((prev) => ({ ...prev, where_laundry: "" }));
        setDirty(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowWhereLaundry]);

  // If housing type changes away from rent, clear landlord selection
  useEffect(() => {
    if (!form) return;
    if (!shouldShowLandlord) {
      if (form.landlord_id || landlordQuery) {
        setForm((prev) => ({ ...prev, landlord_id: "" }));
        setLandlordQuery("");
        setDirty(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowLandlord]);

  // If pastor visit is not Yes, clear denomination
  useEffect(() => {
    if (!form) return;
    if (!shouldShowDenomination) {
      if ((form.church_denom_preferred || "").trim()) {
        setForm((prev) => ({ ...prev, church_denom_preferred: "" }));
        setDirty(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowDenomination]);

  // If attends church in Clare is unchecked, clear church_name
  useEffect(() => {
    if (!form) return;
    if (!shouldShowChurchName) {
      if ((form.church_name || "").trim()) {
        setForm((prev) => ({ ...prev, church_name: "" }));
        setDirty(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowChurchName]);

  // Landlord helpers
  const selectedLandlord = useMemo(() => {
    const lid = form?.landlord_id;
    if (!lid) return null;
    return landlords.find((l) => String(l.id) === String(lid)) || null;
  }, [form?.landlord_id, landlords]);

  const exactMatchLandlordByName = useMemo(() => {
    const q = (landlordQuery || "").trim().toLowerCase();
    if (!q) return null;
    return landlords.find((l) => (l.name || "").trim().toLowerCase() === q) || null;
  }, [landlordQuery, landlords]);

  const landlordTextChanged = async (value) => {
    setLandlordQuery(value);
    setDirty(true);
    setSuccess("");

    // Clear selection until a known match is selected
    setForm((prev) => ({ ...prev, landlord_id: "" }));

    // server-side search to populate suggestions
    await fetchLandlords(value);
  };

  const handleLandlordBlurOrPick = (value) => {
    const q = (value || "").trim();
    setLandlordQuery(q);
    setDirty(true);
    setSuccess("");

    const found =
      landlords.find((l) => (l.name || "").trim().toLowerCase() === q.toLowerCase()) || null;

    setForm((prev) => ({ ...prev, landlord_id: found ? found.id : "" }));
  };

  const openAddLandlord = () => {
    setLandlordModalError("");
    setLandlordModalMode("add");
    setLandlordForm({
      ...EMPTY_LANDLORD_FORM,
      name: (landlordQuery || "").trim(),
    });
    setShowLandlordModal(true);
  };

  const openEditLandlord = () => {
    if (!selectedLandlord) return;
    setLandlordModalError("");
    setLandlordModalMode("edit");
    setLandlordForm({
      id: selectedLandlord.id,
      name: selectedLandlord.name || "",
      phone: selectedLandlord.phone || "",
      address: selectedLandlord.address || "",
      city: selectedLandlord.city || "",
      state: selectedLandlord.state || "MI",
      zipcode: selectedLandlord.zipcode || "",
    });
    setShowLandlordModal(true);
  };

  const saveLandlordFromModal = async () => {
    setLandlordModalSaving(true);
    setLandlordModalError("");

    const payload = {
      name: (landlordForm.name || "").trim(),
      phone: (landlordForm.phone || "").trim(), // REQUIRED
      address: (landlordForm.address || "").trim() || null,
      city: (landlordForm.city || "").trim() || null,
      state: (landlordForm.state || "").trim() || null,
      zipcode: (landlordForm.zipcode || "").trim() || null,
    };

    if (!payload.name) {
      setLandlordModalError("Landlord name is required.");
      setLandlordModalSaving(false);
      return;
    }

    if (!payload.phone) {
      setLandlordModalError("Landlord phone is required.");
      setLandlordModalSaving(false);
      return;
    }

    try {
      let res;
      if (landlordModalMode === "add") {
        res = await api.post("/clients/landlords", payload);
      } else {
        if (!landlordForm.id) {
          setLandlordModalError("Missing landlord id.");
          setLandlordModalSaving(false);
          return;
        }
        res = await api.patch(`/clients/landlords/${landlordForm.id}`, payload);
      }

      const saved = res.data;

      // refresh list to pick up new/updated data
      await fetchLandlords("");

      // select the saved landlord on the client form
      setLandlordQuery(saved.name || payload.name);
      setForm((prev) => ({ ...prev, landlord_id: saved.id }));
      setDirty(true);

      setShowLandlordModal(false);
    } catch (e) {
      setLandlordModalError(
        landlordModalMode === "add" ? "Failed to create landlord." : "Failed to update landlord."
      );
    } finally {
      setLandlordModalSaving(false);
    }
  };

  // Household member helpers
  const addFamilyMemberRow = () => {
    setFamilyMembers((prev) => [
      ...prev,
      { full_name: "", is_child: false, relationship: "", birth_date: "", notes: "" },
    ]);
  };

  const updateFamilyMemberLocal = (idx, field, value) => {
    setFamilyMembers((prev) => prev.map((m, i) => (i === idx ? { ...m, [field]: value } : m)));
  };

  const saveFamilyMember = async (member, idx) => {
    setFmError("");
    setFmSuccess("");

    const payload = {
      full_name: (member.full_name || "").trim(),
      is_child: !!member.is_child,
      relationship: (member.relationship || "").trim(),
      birth_date: member.birth_date || null,
      notes: member.notes || null,
      version: member.version,  // optimistic-lock (ignored on create)
    };

    if (!payload.full_name || !payload.relationship) {
      setFmError("Full Name and Relationship are required.");
      return;
    }

    try {
      if (member.id) {
        const r = await api.patch(`/clients/family-members/${member.id}`, payload);
        setFamilyMembers((prev) => prev.map((m, i) => (i === idx ? r.data : m)));
      } else {
        const r = await api.post(`/clients/${id}/family-members`, payload);
        setFamilyMembers((prev) => prev.map((m, i) => (i === idx ? r.data : m)));
      }
      setFmSuccess("Household member saved");
    } catch (e) {
      if (e?.response?.status === 409) {
        setFmError("This household member was changed by someone else since you opened this page. Please reload and re-apply your changes.");
      } else {
        setFmError("Failed to save household member");
      }
    }
  };

  // UPDATED: verify prompt required before delete
  const deleteFamilyMember = async (member, idx) => {
    setFmError("");
    setFmSuccess("");

    const name = (member?.full_name || "").trim() || "this person";
    const ok = window.confirm(`Delete household member "${name}"? This cannot be undone.`);
    if (!ok) return;

    if (!member?.id) {
      setFamilyMembers((prev) => prev.filter((_, i) => i !== idx));
      setFmSuccess("Household member deleted");
      return;
    }

    try {
      await api.delete(`/clients/family-members/${member.id}`);
      setFamilyMembers((prev) => prev.filter((_, i) => i !== idx));
      setFmSuccess("Household member deleted");
    } catch (e) {
      setFmError("Failed to delete household member");
    }
  };

  const handleBack = () => {
    if (dirty) {
      const confirmLeave = window.confirm(
        "You have unsaved changes. Discard them and leave this page?"
      );
      if (!confirmLeave) return;
    }
    navigate("/clients");
  };

  const doSave = async () => {
    if (!client || !form) return false;
    setSaving(true);
    setError("");
    setSuccess("");

    // kept for later page
    const normalizedHelp = (() => {
      const arr = Array.isArray(form.help_already_received) ? [...form.help_already_received] : [];
      let cleaned = arr.filter((v) => !(typeof v === "string" && v.startsWith("Other: ")));

      const otherChecked = cleaned.includes("Other");
      const otherText = (helpOtherText || "").trim();

      if (otherChecked && otherText) {
        cleaned = cleaned.filter((v) => v !== "Other");
        cleaned.push(`Other: ${otherText}`);
      }

      return Array.from(new Set(cleaned));
    })();

    // kept for later page
    const normalizedFamilyHelpAmount = form.family_help
      ? form.family_help_amount === "" || form.family_help_amount == null
        ? null
        : Number(form.family_help_amount)
      : null;

    // Denomination handling: if they chose "Other (type)", use the free-text field
    const denomSelected = (form.church_denom_preferred || "").trim();
    const denomOtherText = (form.church_denom_preferred_other || "").trim();
    const normalizedDenomination =
      shouldShowDenomination && denomSelected
        ? denomSelected === "Other (type)"
          ? denomOtherText || null
          : denomSelected
        : null;

    const payload = {
      ...form,
      version: client.version,
      intake_date: form.intake_date || null,
      birth_date: form.birth_date || null,
      functional_limitations: (form.functional_limitations || "").trim() || null,
      safety_alerts: (form.safety_alerts || "").trim() || null,
      special_circumstances: (form.special_circumstances || "").trim() || null,

      // Unchecking clears the date so a stale one can't outlive the flag.
      salvation_army_help: !!form.salvation_army_help,
      salvation_army_help_date: form.salvation_army_help
        ? form.salvation_army_help_date || null
        : null,

      housing_type:
        form.housing_type === "" || form.housing_type == null ? null : Number(form.housing_type),

      landlord_id: shouldShowLandlord
        ? form.landlord_id === "" || form.landlord_id == null
          ? null
          : Number(form.landlord_id)
        : null,

      // kept for later page
      help_already_received: normalizedHelp,
      family_help: !!form.family_help,
      family_help_amount: normalizedFamilyHelpAmount,

      // Washer/Dryer + conditional
      has_washer_dryer: form.has_washer_dryer === "" ? null : !!form.has_washer_dryer,
      where_laundry:
        form.has_washer_dryer === false ? (form.where_laundry || "").trim() || null : null,

      transportation_type: (form.transportation_type || "").trim() || null,

      rider_category: form.rider_category || null,

      has_valid_drivers_license:
        form.has_valid_drivers_license === "" ? null : !!form.has_valid_drivers_license,

      uses_transit: form.uses_transit === "" ? null : !!form.uses_transit,
      transit_fare: form.uses_transit === true ? (form.transit_fare || null) : null,

      // NEW: Pastor visit + denomination + church name
      wants_pastor_visit: form.wants_pastor_visit === "" ? null : !!form.wants_pastor_visit,
      is_veteran: form.is_veteran === "" ? null : !!form.is_veteran,
      veteran_names: form.is_veteran === true ? (form.veteran_names || "").trim() || null : null,
      church_denom_preferred: normalizedDenomination,
      church_name: shouldShowChurchName ? (form.church_name || "").trim() || null : null,
    };

    try {
      const res = await api.put(`/clients/${client.id}`, payload);

      setClient(res.data);

      const har2 = Array.isArray(res.data.help_already_received)
        ? res.data.help_already_received
        : [];
      const otherDetail2 = har2.find((v) => typeof v === "string" && v.startsWith("Other: "));
      setHelpOtherText(otherDetail2 ? otherDetail2.replace(/^Other:\s*/, "") : "");

      // Determine if saved denomination matches a known option; otherwise populate the "other" field
      const savedDenom = res.data.church_denom_preferred || "";
      const savedDenomIsKnown = DENOMINATION_OPTIONS.includes(savedDenom);

      setForm({
        ...res.data,
        intake_date: normalizeDateInput(res.data.intake_date),
        birth_date: normalizeDateInput(res.data.birth_date),
        ssn: res.data.ssn || "",
        functional_limitations: res.data.functional_limitations || "",
        safety_alerts: res.data.safety_alerts || "",
        special_circumstances: res.data.special_circumstances || "",
        salvation_army_help: !!res.data.salvation_army_help,
        salvation_army_help_date: normalizeDateInput(res.data.salvation_army_help_date),
        housing_type: res.data.housing_type ?? "",
        landlord_id: res.data.landlord_id ?? "",
        help_already_received: har2,
        family_help: !!res.data.family_help,
        family_help_amount:
          res.data.family_help_amount == null ? "" : String(res.data.family_help_amount),

        has_washer_dryer:
          res.data.has_washer_dryer === true ? true : res.data.has_washer_dryer === false ? false : "",

        where_laundry: res.data.where_laundry || "",

        transportation_type: res.data.transportation_type || "",

        rider_category: res.data.rider_category || "",

        has_valid_drivers_license:
          res.data.has_valid_drivers_license === true
            ? true
            : res.data.has_valid_drivers_license === false
            ? false
            : "",

        uses_transit:
          res.data.uses_transit === true ? true : res.data.uses_transit === false ? false : "",
        transit_fare: res.data.transit_fare || "",

        // NEW: Pastor + denomination + church
        wants_pastor_visit:
          res.data.wants_pastor_visit === true
            ? true
            : res.data.wants_pastor_visit === false
            ? false
            : "",
        church_denom_preferred: savedDenomIsKnown ? savedDenom : savedDenom ? "Other (type)" : "",
        church_denom_preferred_other: savedDenomIsKnown ? "" : savedDenom || "",
        church_name: res.data.church_name || "",
        is_veteran:
          res.data.is_veteran === true ? true : res.data.is_veteran === false ? false : "",
        veteran_names: res.data.veteran_names || "",
      });

      if (res.data.landlord?.name) setLandlordQuery(res.data.landlord.name);
      else if (res.data.landlord_id) {
        const l = landlords.find((x) => String(x.id) === String(res.data.landlord_id));
        if (l?.name) setLandlordQuery(l.name);
      } else {
        setLandlordQuery("");
      }

      setDirty(false);
      setSuccess("Changes saved");
      return true;
    } catch (err) {
      if (err.response?.status === 409) {
        setError("This record was changed by someone else. Please reload and re-apply your changes.");
      } else {
        setError("Failed to save changes");
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const ok = await doSave();
    if (ok) navigate(`/clients/${client.id}/dashboard`);
  };

  if (loading) return <Spinner animation="border" />;
  if (!client || !form) return <Alert variant="danger">Client not found</Alert>;

  const canOpenAddLandlord =
    shouldShowLandlord && !!(landlordQuery || "").trim() && !exactMatchLandlordByName;
  const canOpenEditLandlord = shouldShowLandlord && !!selectedLandlord;

  const bottomSaveDisabled = saving || !dirty;

  return (
    <>
      <NavGuardModal
        show={showModal}
        saving={saving}
        error={error}
        onSave={async () => { const ok = await doSave(); if (ok) resolveGuard(true); }}
        onDiscard={() => { setDirty(false); resolveGuard(true); }}
        onCancel={() => resolveGuard(false)}
      />

      {/* Landlord Add/Edit Modal */}
      <Modal show={showLandlordModal} onHide={() => setShowLandlordModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{landlordModalMode === "add" ? "Add Landlord" : "Edit Landlord"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {landlordModalError && <Alert variant="danger">{landlordModalError}</Alert>}

          <Row>
            <Col md={6} sm={12}>
              <Form.Group className="mb-3" controlId="landlordName">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.name}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6} sm={12}>
              <Form.Group className="mb-3" controlId="landlordPhone">
                <Form.Label>
                  Phone <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.phone}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="Required"
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6} sm={12}>
              <Form.Group className="mb-3" controlId="landlordAddress">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.address}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>

            <Col md={6} sm={12}>
              <Form.Group className="mb-3" controlId="landlordCity">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.city}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, city: e.target.value }))}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4} sm={12}>
              <Form.Group className="mb-3" controlId="landlordState">
                <Form.Label>State</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.state}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, state: e.target.value }))}
                />
              </Form.Group>
            </Col>

            <Col md={4} sm={12}>
              <Form.Group className="mb-3" controlId="landlordZip">
                <Form.Label>Zipcode</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.zipcode}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, zipcode: e.target.value }))}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLandlordModal(false)}>
            Cancel
          </Button>
          <Button
            onClick={saveLandlordFromModal}
            disabled={
              landlordModalSaving ||
              !(landlordForm.name || "").trim() ||
              !(landlordForm.phone || "").trim()
            }
          >
            {landlordModalSaving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Col>
          <h3 className="mb-0">Edit Demographics for {form.first_name} {form.last_name}</h3>
          <div className="text-muted">Client ID: {id}</div>
        </Col>
        <div>
          <Button variant="outline-secondary" className="me-2" onClick={handleBack}>
            Back to Clients
          </Button>
          <Button onClick={handleSave} disabled={saving || !dirty}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {error && !showModal && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card>
        <Card.Body>
          <Form>
            {/* Intake Date */}
            <Row className="align-items-end">
              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="intakeDate">
                  <Form.Label>Intake Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.intake_date || ""}
                    onChange={handleChange("intake_date")}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Name */}
            <Row>
              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="firstName">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.first_name || ""}
                    onChange={handleChange("first_name")}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="middleName">
                  <Form.Label>Middle Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.middle_name || ""}
                    onChange={handleChange("middle_name")}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="lastName">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.last_name || ""}
                    onChange={handleChange("last_name")}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Birth Date + SSN + Phones */}
            <Row>
              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="birthDate">
                  <Form.Label>Birth Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.birth_date || ""}
                    onChange={handleChange("birth_date")}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="ssn">
                  <Form.Label>SS#</Form.Label>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="###-##-####"
                    value={form.ssn || ""}
                    onChange={handleChange("ssn")}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="homePhone">
                  <Form.Label>Home Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.home_phone || ""}
                    onChange={handleChange("home_phone")}
                    disabled={!!form.no_phone}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="mobilePhone">
                  <Form.Label>Mobile Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.mobile_phone || ""}
                    onChange={handleChange("mobile_phone")}
                    disabled={!!form.no_phone}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="noPhone">
                  <Form.Label>No Phone</Form.Label>
                  <Form.Check
                    type="checkbox"
                    className="mt-2"
                    label="Client has no phone"
                    checked={!!form.no_phone}
                    onChange={handleCheckbox("no_phone")}
                  />
                  <Form.Text className="text-muted">
                    Disables the phone fields; satisfies the Partial-Intake phone requirement.
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="emergencyContactName">
                  <Form.Label>Emergency Contact Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.emergency_contact_name || ""}
                    onChange={handleChange("emergency_contact_name")}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="emergencyContactPhone">
                  <Form.Label>Emergency Contact Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.emergency_contact_phone || ""}
                    onChange={handleChange("emergency_contact_phone")}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Functional Limitations + Safety Alerts + Special Circumstances.
                Three across on md+; placeholders kept short so the narrower
                boxes stay readable. */}
            <Row>
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="functionalLimitations">
                  <Form.Label>Functional Limitations</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.functional_limitations || ""}
                    onChange={handleChange("functional_limitations")}
                    placeholder="e.g., Wheelchair, hearing impairment"
                  />
                </Form.Group>
              </Col>

              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="safetyAlerts">
                  <Form.Label>Safety Alerts</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.safety_alerts || ""}
                    onChange={handleChange("safety_alerts")}
                    placeholder="e.g., No home visits, do not leave alone"
                  />
                </Form.Group>
              </Col>

              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="specialCircumstances">
                  <Form.Label>Special Circumstances</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.special_circumstances || ""}
                    onChange={handleChange("special_circumstances")}
                    placeholder="e.g., New baby, recently widowed"
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Received help from Salvation Army. The date drives a badge on the
                client dashboard that expires after one year. */}
            <Row className="align-items-end">
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="salvationArmyHelp">
                  <Form.Check
                    type="checkbox"
                    label="Received help from Salvation Army"
                    checked={!!form.salvation_army_help}
                    onChange={toggleSalvationArmyHelp}
                  />
                </Form.Group>
              </Col>

              {form.salvation_army_help ? (
                <Col md={3} sm={12}>
                  <Form.Group className="mb-3" controlId="salvationArmyHelpDate">
                    <Form.Label>Date Help Received</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.salvation_army_help_date || ""}
                      onChange={handleChange("salvation_army_help_date")}
                      max={todayISO()}
                    />
                    <Form.Text className="text-muted">
                      The badge shows for one year from this date.
                    </Form.Text>
                  </Form.Group>
                </Col>
              ) : null}
            </Row>

            {/* Address */}
            <Row>
              <Col md={5} sm={12}>
                <Form.Group className="mb-3" controlId="address1">
                  <Form.Label>Address Line 1</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.address_line1 || ""}
                    onChange={handleChange("address_line1")}
                  />
                </Form.Group>
              </Col>

              <Col md={5} sm={12}>
                <Form.Group className="mb-3" controlId="address2">
                  <Form.Label>Address Line 2</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.address_line2 || ""}
                    onChange={handleChange("address_line2")}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* City/State/ZIP/County */}
            <Row>
              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="city">
                  <Form.Label>City</Form.Label>
                  <Form.Control type="text" value={form.city || ""} onChange={handleChange("city")} />
                </Form.Group>
              </Col>

              <Col md={2} sm={3}>
                <Form.Group className="mb-3" controlId="state">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.state || ""}
                    onChange={handleChange("state")}
                    placeholder="MI"
                  />
                </Form.Group>
              </Col>

              <Col md={2} sm={3}>
                <Form.Group className="mb-3" controlId="postalCode">
                  <Form.Label>ZIP</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.postal_code || ""}
                    onChange={handleChange("postal_code")}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="county">
                  <Form.Label>County</Form.Label>
                  <Form.Select value={form.county || "Clare"} onChange={handleChange("county")}>
                    {COUNTY_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Housing Type */}
            <Row>
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="housingType">
                  <Form.Label>Housing Type</Form.Label>
                  <Form.Select
                    value={form.housing_type ?? ""}
                    onChange={handleChange("housing_type")}
                    disabled={housingTypesLoading}
                  >
                    <option value="">
                      {housingTypesLoading ? "Loading..." : "Select housing type..."}
                    </option>

                    {housingTypes.map((ht) => (
                      <option key={ht.id} value={ht.id}>
                        {getHousingTypeLabel(ht) || `Type #${ht.id}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Landlord */}
            {shouldShowLandlord && (
              <Card className="mb-3">
                <Card.Body>
                  <Row className="align-items-end">
                    <Col md={6} sm={12}>
                      <Form.Group className="mb-2" controlId="landlordLookup">
                        <Form.Label>Landlord</Form.Label>
                        <Form.Control
                          type="text"
                          list="landlordList"
                          value={landlordQuery}
                          onChange={(e) => landlordTextChanged(e.target.value)}
                          onBlur={(e) => handleLandlordBlurOrPick(e.target.value)}
                          placeholder={landlordsLoading ? "Loading landlords..." : "Select or type landlord..."}
                        />
                        <datalist id="landlordList">
                          {landlords.map((l) => (
                            <option key={l.id} value={l.name} />
                          ))}
                        </datalist>

                        <Form.Text className="text-muted">
                          Select an existing landlord, or type a new name and click “Add New Landlord”.
                        </Form.Text>
                      </Form.Group>
                    </Col>

                    <Col md={6} sm={12}>
                      <div className="d-flex gap-2 mb-2">
                        <Button
                          variant="outline-primary"
                          onClick={openAddLandlord}
                          disabled={!canOpenAddLandlord}
                        >
                          + Add New Landlord
                        </Button>

                        <Button
                          variant="outline-secondary"
                          onClick={openEditLandlord}
                          disabled={!canOpenEditLandlord}
                        >
                          Edit Selected Landlord
                        </Button>
                      </div>

                      {selectedLandlord && (
                        <div className="text-muted" style={{ fontSize: "0.9rem" }}>
                          <div>
                            <strong>Selected:</strong> {selectedLandlord.name}
                          </div>
                          {selectedLandlord.phone && (
                            <div>
                              <strong>Phone:</strong> {selectedLandlord.phone}
                            </div>
                          )}
                          {(selectedLandlord.address ||
                            selectedLandlord.city ||
                            selectedLandlord.state ||
                            selectedLandlord.zipcode) && (
                            <div>
                              <strong>Address:</strong>{" "}
                              {[
                                selectedLandlord.address,
                                selectedLandlord.city,
                                selectedLandlord.state,
                                selectedLandlord.zipcode,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </div>
                          )}
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Flags */}
            <Form.Group className="mb-3" controlId="flags">
              <Form.Check
                type="checkbox"
                label="Lives in Clare County"
                checked={!!form.lives_in_clare}
                onChange={handleCheckbox("lives_in_clare")}
              />
              <Form.Check
                type="checkbox"
                label="Attends church in Clare County"
                checked={!!form.attends_church_in_clare}
                onChange={handleCheckbox("attends_church_in_clare")}
              />
              <Form.Check
                type="checkbox"
                label="Children attend school in Clare County"
                checked={!!form.children_school_in_clare}
                onChange={handleCheckbox("children_school_in_clare")}
              />
              <Form.Check
                type="checkbox"
                label="Permission to verify information"
                checked={!!form.permission_to_verify}
                onChange={handleCheckbox("permission_to_verify")}
              />
            </Form.Group>

            {/* New: Church Name (only when attends church in Clare) */}
            {shouldShowChurchName && (
              <Row>
                <Col md={6} sm={12}>
                  <Form.Group className="mb-3" controlId="churchName">
                    <Form.Label>Name of church</Form.Label>
                    <Form.Control
                      type="text"
                      list="churchNameOptions"
                      value={form.church_name || ""}
                      onChange={handleChange("church_name")}
                      placeholder="Select or type..."
                    />
                    <datalist id="churchNameOptions">
                      {CHURCH_SUGGESTIONS.map((c) => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                    <Form.Text className="text-muted">
                      You can select from the list or type a new church name.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* Referred By */}
            <Row>
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="referredBy">
                  <Form.Label>Referred By</Form.Label>
                  <Form.Control
                    type="text"
                    list="referredByOptions"
                    value={form.referred_by || ""}
                    onChange={handleChange("referred_by")}
                    placeholder="Select or type..."
                  />
                  <datalist id="referredByOptions">
                    {REFERRED_BY_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} />
                    ))}
                  </datalist>
                </Form.Group>
              </Col>
            </Row>

            {/* Household Members */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">People living in the home</h5>
                <Button variant="outline-primary" size="sm" onClick={addFamilyMemberRow}>
                  + Add Person
                </Button>
              </div>

              {fmLoading && <div className="text-muted mb-2">Loading household members…</div>}
              {fmError && <Alert variant="danger">{fmError}</Alert>}
              {fmSuccess && <Alert variant="success">{fmSuccess}</Alert>}

              <Table bordered hover size="sm" responsive>
                <thead>
                  <tr>
                    <th style={{ width: "22%" }}>Full Name</th>
                    <th style={{ width: "9%", textAlign: "center" }}>Is Child</th>
                    <th style={{ width: "14%" }}>Relationship</th>
                    <th style={{ width: "12%" }}>Birth Date</th>
                    <th>Notes</th>
                    <th style={{ width: "18%" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {familyMembers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-3">
                        No household members added yet.
                      </td>
                    </tr>
                  ) : (
                    familyMembers.map((m, idx) => (
                      <tr key={m.id || `new-${idx}`}>
                        <td>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={m.full_name || ""}
                            onChange={(e) => updateFamilyMemberLocal(idx, "full_name", e.target.value)}
                            placeholder="Full name"
                          />
                        </td>

                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                          <Form.Select
                            size="sm"
                            value={m.is_child ? "yes" : "no"}
                            onChange={(e) =>
                              updateFamilyMemberLocal(idx, "is_child", e.target.value === "yes")
                            }
                          >
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </Form.Select>
                        </td>

                        <td>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={m.relationship || ""}
                            onChange={(e) =>
                              updateFamilyMemberLocal(idx, "relationship", e.target.value)
                            }
                            placeholder="e.g., Spouse, Child"
                          />
                        </td>

                        <td>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={normalizeDateInput(m.birth_date) || ""}
                            onChange={(e) => updateFamilyMemberLocal(idx, "birth_date", e.target.value)}
                          />
                        </td>

                        <td>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={m.notes || ""}
                            onChange={(e) => updateFamilyMemberLocal(idx, "notes", e.target.value)}
                            placeholder="Optional notes"
                          />
                        </td>

                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => saveFamilyMember(m, idx)}
                              disabled={!(m.full_name || "").trim() || !(m.relationship || "").trim()}
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => deleteFamilyMember(m, idx)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            {/* Demographics: Washer/Dryer -> Where Laundry (conditional) -> Transportation -> Driver's License */}
            <Row className="mt-3">
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="washerDryer">
                  <Form.Label>Has Washer and Dryer</Form.Label>
                  <Form.Select
                    value={
                      form.has_washer_dryer === true ? "yes" : form.has_washer_dryer === false ? "no" : ""
                    }
                    onChange={handleBooleanSelect("has_washer_dryer")}
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {shouldShowWhereLaundry && (
              <Row>
                <Col md={6} sm={12}>
                  <Form.Group className="mb-3" controlId="whereLaundry">
                    <Form.Label>Where do you do laundry?</Form.Label>
                    <Form.Control
                      type="text"
                      value={form.where_laundry || ""}
                      onChange={handleChange("where_laundry")}
                      placeholder="e.g., Laundromat in Clare, Family member, Neighbor, etc."
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row>
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="transportationType">
                  <Form.Label>What transportation do you have?</Form.Label>
                  <Form.Select
                    value={form.transportation_type || ""}
                    onChange={handleChange("transportation_type")}
                  >
                    <option value="">Select...</option>
                    {TRANSPORTATION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="riderCategory">
                  <Form.Label>Rider Category</Form.Label>
                  <Form.Select
                    value={form.rider_category || ""}
                    onChange={handleChange("rider_category")}
                  >
                    <option value="">Select...</option>
                    <option value="MAN">MAN</option>
                    <option value="WOMAN">WOMAN</option>
                    <option value="COUPLE">COUPLE</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Default used for each transportation request (changeable per ride).
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="driverLicense">
                  <Form.Label>Do you have a valid driver's license?</Form.Label>
                  <Form.Select
                    value={
                      form.has_valid_drivers_license === true
                        ? "yes"
                        : form.has_valid_drivers_license === false
                        ? "no"
                        : ""
                    }
                    onChange={handleBooleanSelect("has_valid_drivers_license")}
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="usesTransit">
                  <Form.Label>Uses Transit</Form.Label>
                  <Form.Select
                    value={
                      form.uses_transit === true ? "yes" : form.uses_transit === false ? "no" : ""
                    }
                    onChange={handleBooleanSelect("uses_transit")}
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {form.uses_transit === true && (
                <Col md={3} sm={6}>
                  <Form.Group className="mb-3" controlId="transitFare">
                    <Form.Label>Transit Fare</Form.Label>
                    <Form.Select
                      value={form.transit_fare || ""}
                      onChange={(e) =>
                        handleChange("transit_fare")({ target: { value: e.target.value } })
                      }
                    >
                      <option value="">Select...</option>
                      <option value="1">$1 ride</option>
                      <option value="3">$3 ride</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
            </Row>

            {/* New: Pastor visit + denomination (conditional) */}
            <Row>
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="pastorVisit">
                  <Form.Label>Would you like a pastor to visit you?</Form.Label>
                  <Form.Select
                    value={
                      form.wants_pastor_visit === true
                        ? "yes"
                        : form.wants_pastor_visit === false
                        ? "no"
                        : ""
                    }
                    onChange={handleBooleanSelect("wants_pastor_visit")}
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {shouldShowDenomination && (
              <>
                <Row>
                  <Col md={4} sm={12}>
                    <Form.Group className="mb-3" controlId="preferredDenomination">
                      <Form.Label>Preferred Denomination</Form.Label>
                      <Form.Select
                        value={form.church_denom_preferred || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((prev) => ({
                            ...prev,
                            church_denom_preferred: v,
                            church_denom_preferred_other:
                              v === "Other (type)" ? prev.church_denom_preferred_other || "" : "",
                          }));
                          setDirty(true);
                          setSuccess("");
                        }}
                      >
                        <option value="">Select...</option>
                        {DENOMINATION_OPTIONS.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {(form.church_denom_preferred || "") === "Other (type)" && (
                  <Row>
                    <Col md={6} sm={12}>
                      <Form.Group className="mb-3" controlId="preferredDenominationOther">
                        <Form.Label>Other Denomination (type)</Form.Label>
                        <Form.Control
                          type="text"
                          value={form.church_denom_preferred_other || ""}
                          onChange={handleChange("church_denom_preferred_other")}
                          placeholder="Enter denomination"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}
              </>
            )}

            {/* Veteran status */}
            <Row>
              <Col md={4} sm={12}>
                <Form.Group className="mb-3" controlId="isVeteran">
                  <Form.Label>
                    Are you or is anyone in your household a United States Veteran?
                  </Form.Label>
                  <Form.Select
                    value={
                      form.is_veteran === true ? "yes" : form.is_veteran === false ? "no" : ""
                    }
                    onChange={handleBooleanSelect("is_veteran")}
                  >
                    <option value="">Select...</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {form.is_veteran === true && (
              <Row>
                <Col md={5} sm={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Select Veteran Name(s)</Form.Label>
                    {veteranOptions.length === 0 ? (
                      <p className="text-muted small">No household members found. Save the client name first.</p>
                    ) : (
                      <div className="border rounded" style={{ overflow: "hidden" }}>
                        {veteranOptions.map((name) => {
                          const selected = selectedVeteranNames.includes(name);
                          return (
                            <div
                              key={name}
                              onClick={() => toggleVeteranName(name)}
                              style={{
                                cursor: "pointer",
                                padding: "0.5rem 0.75rem",
                                userSelect: "none",
                                backgroundColor: selected ? "#0d6efd" : "",
                                color: selected ? "#fff" : "inherit",
                                borderBottom: "1px solid #dee2e6",
                              }}
                            >
                              {selected && <i className="bi bi-check2 me-2" />}
                              {name}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            )}

            {/* Bottom Save button */}
            <div className="d-flex justify-content-end mt-4">
              <Button onClick={handleSave} disabled={bottomSaveDisabled}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
}
